import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getProjectPayments, createProjectPayment } from '@services/projects.service';
import {
    formatDate,
    formatPaymentAmount,
    getPaymentSummary,
    getPaymentTotalDisplay,
    getPaymentRowKey,
    getInstallmentsLabel,
} from '@hooks/projects/projectHelpers';
import { useCanCheck } from '@/ability/helpers';
import { PROJECT_PAYMENTS_VIEW, PROJECT_PAYMENTS_UPDATE } from '@/ability';
import { invalidateProjectQueries } from './projectQueryInvalidation';

export const PROJECT_PAYMENTS_QUERY_KEY = (projectId) => ['projects', projectId, 'payments'];

/** Sort by date descending (newest first). */
function sortPaymentsNewestFirst(items) {
    if (!Array.isArray(items) || items.length === 0) return items;
    return [...items].sort((a, b) => {
        const ta = a.createdAt ?? a.date ?? a.paidAt ?? 0;
        const tb = b.createdAt ?? b.date ?? b.paidAt ?? 0;
        return new Date(tb).getTime() - new Date(ta).getTime();
    });
}

/** Table column config for payments (uses projectHelpers for formatting). */
function buildPaymentTableColumns() {
    return [
        {
            title: 'Date',
            dataIndex: 'createdAt',
            width: 140,
            render: (d, r) => formatDate(d ?? r?.date ?? r?.paidAt) ?? '—',
        },
        { title: 'Amount', dataIndex: 'amount', width: 140, render: (amt, r) => formatPaymentAmount(amt, r?.currency) },
        { title: 'Currency', dataIndex: 'currency', width: 100, render: (c) => c || '—' },
        { title: 'Notes', dataIndex: 'notes', ellipsis: true, render: (n) => n || '—' },
    ];
}

export const PAYMENT_CURRENCY_OPTIONS = [
    { value: 'USD', label: 'USD' },
    { value: 'EUR', label: 'EUR' },
    { value: 'GBP', label: 'GBP' },
    { value: 'PKR', label: 'PKR' },
];

/**
 * Validates form values and builds API body. Returns { valid: boolean, body?: object, error?: string }.
 */
function validateAndBuildPaymentBody(values) {
    const amount = values?.amount != null ? Number(values.amount) : 0;
    if (amount <= 0) return { valid: false, error: 'Enter a valid amount' };
    const currency = (values?.currency && String(values.currency).trim()) || 'USD';
    const notes = values?.notes != null ? String(values.notes).trim() : '';
    return { valid: true, body: { amount, currency, notes: notes || undefined } };
}

/**
 * Project payments tab: list and create installments. RBAC: view gated by projects.payment.view, add/record payment gated by projects.payment.update.
 */
export function useProjectPayments(projectId) {
    const queryClient = useQueryClient();
    const can = useCanCheck();
    const canView = can(PROJECT_PAYMENTS_VIEW);
    const canUpdate = can(PROJECT_PAYMENTS_UPDATE);

    const {
        data: payments = [],
        isLoading,
        isError,
        refetch,
    } = useQuery({
        queryKey: PROJECT_PAYMENTS_QUERY_KEY(projectId),
        queryFn: () => getProjectPayments(projectId),
        enabled: !!projectId && canView,
    });

    const paymentsSorted = useMemo(() => sortPaymentsNewestFirst(Array.isArray(payments) ? payments : []), [payments]);

    const summary = useMemo(() => getPaymentSummary(paymentsSorted), [paymentsSorted]);
    const totalDisplay = useMemo(() => getPaymentTotalDisplay(paymentsSorted), [paymentsSorted]);
    const installmentsLabel = useMemo(
        () => getInstallmentsLabel(summary.installmentsCount),
        [summary.installmentsCount],
    );

    const paymentTableColumns = useMemo(() => buildPaymentTableColumns(), []);
    const tableData = useMemo(() => paymentsSorted.map((p) => ({ ...p, key: getPaymentRowKey(p) })), [paymentsSorted]);

    const createMutation = useMutation({
        mutationFn: (body) => createProjectPayment(projectId, body),
        onSuccess: () => {
            invalidateProjectQueries(queryClient, projectId);
        },
    });

    const submitPayment = (values, callbacks = {}) => {
        const { valid, body, error } = validateAndBuildPaymentBody(values);
        if (!valid) {
            if (callbacks.onValidationError) callbacks.onValidationError(error);
            return;
        }
        createMutation.mutate(body, {
            onSuccess: callbacks.onSuccess,
            onError: callbacks.onError,
        });
    };

    return {
        canView,
        canCreate: canUpdate,
        payments: paymentsSorted,
        isLoading,
        isError,
        refetch,
        summary,
        totalDisplay,
        installmentsLabel,
        paymentTableColumns,
        tableData,
        currencyOptions: PAYMENT_CURRENCY_OPTIONS,
        submitPayment,
        createPaymentPending: createMutation.isPending,
    };
}
